import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Card, CardContent, CardHeader, Typography, CircularProgress, Box, TextField, Button, IconButton, FormControlLabel, Switch, Chip, Stack } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { sensorAlgorithm } from 'wyes-glasses-core/src/processing/sensor-current-algorithm';
import { SESSION_TAGS } from './tags';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFetch } from '../../../hooks/use-fetch';
import { BASE_ADMIN_API_URL } from '../../../config';
import {
    Chart,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Legend,
    Tooltip,
    Filler,
} from 'chart.js';
import AnnotationPlugin from 'chartjs-plugin-annotation';
import { Line } from 'react-chartjs-2';
import { generateChartLineColor } from '../../devices/charts/stream-chart';

Chart.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Legend,
    Tooltip,
    Filler,
    AnnotationPlugin,
);

function parseCsv(text) {
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',');
    return lines.slice(1).map(line => {
        const values = line.split(',');
        const row = {};
        headers.forEach((h, i) => { row[h] = values[i]; });
        return row;
    });
}

function buildPhaseAnnotations(rows) {
    const annotations = {};
    let currentPhase = null;
    let phaseStart = 0;

    rows.forEach((row, i) => {
        if (row.phase !== currentPhase) {
            if (currentPhase !== null) {
                annotations[`phase_${currentPhase}`] = {
                    type: 'box',
                    xMin: phaseStart,
                    xMax: i - 1,
                    backgroundColor: currentPhase === 'baseline' ? 'rgba(100,100,255,0.06)' : 'rgba(255,160,0,0.06)',
                    borderWidth: 0,
                    label: {
                        display: true,
                        content: currentPhase,
                        position: 'start',
                    },
                };
            }
            currentPhase = row.phase;
            phaseStart = i;
        }
    });

    if (currentPhase !== null) {
        annotations[`phase_${currentPhase}`] = {
            type: 'box',
            xMin: phaseStart,
            xMax: rows.length - 1,
            backgroundColor: currentPhase === 'baseline' ? 'rgba(100,100,255,0.06)' : 'rgba(255,160,0,0.06)',
            borderWidth: 0,
            label: {
                display: true,
                content: currentPhase,
                position: 'start',
            },
        };
    }

    return annotations;
}

const EVENT_COLORS = {
    move: { border: 'rgba(76,175,80,0.5)', label: 'rgba(76,175,80,0.7)' },
    involuntary: { border: 'rgba(255,0,0,0.5)', label: 'rgba(255,0,0,0.7)' },
};
const DEFAULT_EVENT_COLOR = { border: 'rgba(150,150,150,0.5)', label: 'rgba(150,150,150,0.7)' };

function buildEventAnnotations(rows) {
    const annotations = {};
    rows.forEach((row, i) => {
        if (row.event && row.event.trim() !== '') {
            const colors = EVENT_COLORS[row.event.trim()] || DEFAULT_EVENT_COLOR;
            annotations[`event_${i}`] = {
                type: 'line',
                xMin: i,
                xMax: i,
                borderColor: colors.border,
                borderWidth: 2,
                label: {
                    display: true,
                    content: row.event,
                    position: 'start',
                    backgroundColor: colors.label,
                    color: 'white',
                },
            };
        }
    });
    return annotations;
}

const DEFAULT_ALGORITHM_PARAMS = { s: 1, f: 10, t: 20, d: 0 };

// Standard 6-sensor glasses layout: index → sensor.n value (matches SENSOR.* i18n keys)
const SENSOR_NAMES = ['left nose', 'left temple', 'left cheek', 'right nose', 'right temple', 'right cheek'];
const POSITION_NAMES = { 0: 'nose', 1: 'temple', 2: 'cheek' };

function SensorChart({ csvText, sessionType, processed, t }) {
    const { chartData, chartOptions } = useMemo(() => {
        const rows = parseCsv(csvText);
        const labels = rows.map((_, i) => i);
        const sensorKeys = ['s0', 's1', 's2', 's3', 's4', 's5'];

        const datasets = sensorKeys.map((key, i) => {
            const color = generateChartLineColor(i);
            let data;
            if (processed) {
                const algo = sensorAlgorithm(DEFAULT_ALGORITHM_PARAMS);
                data = rows.map(r => algo(parseFloat(r[key])));
            } else {
                data = rows.map(r => parseFloat(r[key]));
            }
            return {
                label: SENSOR_NAMES[i] ? t(`SENSOR.${SENSOR_NAMES[i]}`) : key,
                data,
                borderColor: color.line,
                backgroundColor: color.background,
                fill: false,
                pointRadius: 0,
                borderWidth: 1.5,
            };
        });

        const hasEvent = rows.length > 0 && 'event' in rows[0];
        const annotations = hasEvent ? buildEventAnnotations(rows) : buildPhaseAnnotations(rows);

        return {
            chartData: { labels, datasets },
            chartOptions: {
                animation: false,
                maintainAspectRatio: false,
                interaction: { mode: 'nearest', intersect: false },
                elements: { line: { tension: 0 } },
                scales: {
                    x: { type: 'category', ticks: { maxTicksLimit: 20 } },
                    y: {},
                },
                plugins: {
                    annotation: { annotations },
                },
            },
        };
    }, [csvText, processed]);

    return <Line height={300} data={chartData} options={chartOptions} />;
}

function DetectionCard({ detection, t }) {
    if (!detection) return null;

    const sensorName = detection.sensorIndex != null && SENSOR_NAMES[detection.sensorIndex] ? t(`SENSOR.${SENSOR_NAMES[detection.sensorIndex]}`) : null;
    const positionName = detection.sensorPosition != null ? POSITION_NAMES[detection.sensorPosition] : null;

    return (
        <Card>
            <CardHeader title={t('SESSIONS.DETECTION_TITLE')} />
            <CardContent>
                <Grid container spacing={1}>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t('SESSIONS.SENSOR_INDEX')}</Typography><Typography>{sensorName ?? detection.sensorIndex ?? '—'}</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t('SESSIONS.CONFIDENCE')}</Typography><Typography>{detection.confidence != null ? `${Math.round(detection.confidence * 100)}%` : '—'}</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t('SESSIONS.THRESHOLD')}</Typography><Typography>{detection.suggestedThreshold ?? '—'}</Typography></Grid>
                    <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t('SESSIONS.POLARITY')}</Typography><Typography>{detection.polarity ?? '—'}</Typography></Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

function SessionInfoCard({ sessionId, initialNotes, initialTags, fetchWithAuth, t }) {
    const [notes, setNotes] = useState(initialNotes || '');
    const [selectedTags, setSelectedTags] = useState(() => initialTags ? initialTags.split(',').filter(Boolean) : []);
    const [saving, setSaving] = useState(false);

    const toggleTag = (value) => {
        setSelectedTags(prev => prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]);
    };

    async function handleSave() {
        setSaving(true);
        try {
            const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/sessions/${sessionId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ notes, tags: selectedTags.join(',') }),
            });
            if (!response.ok) throw new Error(await response.text());
        } catch (e) {
            console.error('Failed to save session', e);
        } finally {
            setSaving(false);
        }
    }

    return (
        <Card>
            <CardHeader title={t('SESSIONS.SESSION_DATA')} />
            <CardContent>
                <Stack spacing={4}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>{t('SESSIONS.TAGS')}</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {SESSION_TAGS.map(tag => (
                                <Chip
                                    key={tag.value}
                                    label={t(tag.labelKey)}
                                    variant={selectedTags.includes(tag.value) ? 'filled' : 'outlined'}
                                    color={selectedTags.includes(tag.value) ? 'primary' : 'default'}
                                    onClick={() => toggleTag(tag.value)}
                                    size="small"
                                />
                            ))}
                        </Stack>
                    </Box>
                    <TextField
                        multiline
                        minRows={3}
                        fullWidth
                        label={t('SESSIONS.NOTES')}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" onClick={handleSave} disabled={saving}>
                            {t('SESSIONS.SAVE')}
                        </Button>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}

export default function SessionDetails() {
    const { id } = useParams();
    const { t } = useTranslation();
    const [session, setSession] = useState(null);
    const [csvText, setCsvText] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processed, setProcessed] = useState(true);
    const fetchWithAuth = useFetch();

    useEffect(() => {
        let cancelled = false;

        async function load() {
            try {
                const [sessionRes, dataRes] = await Promise.all([
                    fetchWithAuth(`${BASE_ADMIN_API_URL}/sessions/${id}`),
                    fetchWithAuth(`${BASE_ADMIN_API_URL}/sessions/${id}/data`),
                ]);
                if (!sessionRes.ok) throw new Error(await sessionRes.text());
                if (!dataRes.ok) throw new Error(await dataRes.text());
                const sessionData = await sessionRes.json();
                const csv = await dataRes.text();
                if (!cancelled) {
                    setSession(sessionData);
                    setCsvText(csv);
                }
            } catch (e) {
                console.error('Failed to load session', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => { cancelled = true; };
    }, [fetchWithAuth, id]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!session) return null;

    const detection = session.metadata?.detection;

    const handleDownload = async () => {
        try {
            const response = await fetchWithAuth(`${BASE_ADMIN_API_URL}/sessions/${id}/download`);
            if (!response.ok) throw new Error(await response.text());
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${id}.zip`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Failed to download session', e);
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Card>
                    <CardHeader
                        title={t('SESSIONS.CHART_TITLE')}
                        subheader={`${session.device?.id ?? session.device_id} — ${new Date(session.created_at).toLocaleString()}${session.recorded_by ? ` — ${session.recorded_by.name}` : ''}`}
                        action={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={<Switch checked={processed} onChange={(e) => setProcessed(e.target.checked)} size="small" />}
                                    label={t('SESSIONS.PROCESSED_DATA')}
                                />
                                <IconButton onClick={handleDownload}>
                                    <DownloadIcon />
                                </IconButton>
                            </Box>
                        }
                    />
                    <CardContent>
                        {csvText ? <SensorChart csvText={csvText} sessionType={session.type} processed={processed} t={t} /> : <Typography>—</Typography>}
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={6}>
                <SessionInfoCard sessionId={id} initialNotes={session.notes} initialTags={session.tags} fetchWithAuth={fetchWithAuth} t={t} />
            </Grid>
            {session.type !== 'STREAM' && (
                <Grid item xs={12} md={6}>
                    <DetectionCard detection={detection} t={t} />
                </Grid>
            )}
        </Grid>
    );
}
